import { useEffect, useState, useMemo } from 'react';
import {
  useTracking,
  useUpdateDataMeshTransactionId,
  useLogger,
} from '@edreamsodigeo/frontend-core';
import { ItinerarySearchQuery, SearchQueryQuery } from 'generated/graphql';
import { SearchQueryResult } from './types';

interface CreateShoppingCartResponse {
  redirect: boolean;
}
interface CreateShoppingCartState {
  data: CreateShoppingCartResponse | null;
  loading: boolean;
  error: unknown;
}

interface CreateShoppingCartFetcher {
  (data: SearchQueryQuery): Promise<CreateShoppingCartResponse>;
}
export const useTrackErrors = (
  data: SearchQueryQuery | null,
  loading: boolean,
  errors: SearchQueryResult['errors']
): void => {
  const { trackEvent } = useTracking();
  const hasItineraries = (data?.search?.itineraries.length || 0) > 0;
  const searchCode = data?.search?.searchCode;
  useEffect(() => {
    if (!loading && errors) {
      const [error] = errors ?? [];

      switch ((error?.extensions as { code: string })?.code) {
        case 'ERR-01':
          trackEvent('request_errors', 'broken_flow_message');
          trackEvent(
            'request_errors',
            'error:error.validate.segment.currDateLess-code:validation_error'
          );
          break;

        default:
          if (error?.message)
            trackEvent('request_errors', `error:${error.message}`);
      }
    } else if (!loading && !errors && !hasItineraries) {
      trackEvent('searcher_flights_error', 'no_results_for_this_search_error');
      const { code } = searchCode ?? {};
      if (code === 'WNG-024') {
        trackEvent(
          'request_errors',
          `error:${code}-code:no_itineraries_results`
        );
      }
    }
  }, [loading, errors, hasItineraries, trackEvent, searchCode]);
};

export const useUpdateMeshTransactionId = (
  data: SearchQueryQuery | null
): void => {
  const { setTransactionSearchId } = useUpdateDataMeshTransactionId();
  const searchId = data?.search?.searchId;
  useEffect(() => {
    if (searchId) {
      setTransactionSearchId(searchId);
    }
  }, [setTransactionSearchId, searchId]);
};

export const useLoadingResultsPage = (
  loading: boolean,
  onLoadingResultsPage: () => void
): void => {
  useEffect(() => {
    if (loading) {
      onLoadingResultsPage();
    }
  }, [loading, onLoadingResultsPage]);
};

const INITIAL_STATE = {
  loading: false,
  error: null,
  data: null,
};
const LOADING_STATE = {
  ...INITIAL_STATE,
  loading: true,
};

const useCreateMapiShoppingCartQuery = (
  data: ItinerarySearchQuery | null,
  createMapiShoppingCart
): CreateShoppingCartState => {
  const [queryState, setQueryState] = useState(INITIAL_STATE);
  useEffect(() => {
    if (data) {
      if (data.itinerarySearch.externalSelection) {
        setQueryState(LOADING_STATE);
        createMapiShoppingCart(data)
          .then((data) => {
            setQueryState({
              loading: false,
              data,
              error: null,
            });
          })
          .catch((error) => {
            setQueryState({
              loading: false,
              data: null,
              error,
            });
          });
      }
    }
  }, [createMapiShoppingCart, data]);
  return queryState;
};

export const useCreateMapiShoppingCart = (
  createMapiShoppingCart: CreateShoppingCartFetcher,
  onSearchMapiError: () => void,
  // TODO - Update this once we shape the searchRequest type
  searchRequest: any,
  data: ItinerarySearchQuery | null
): { loading: boolean } => {
  const createShoppingCartState = useCreateMapiShoppingCartQuery(
    data,
    createMapiShoppingCart
  );
  const loading =
    !!searchRequest?.itinerary?.externalSelection?.searchId ||
    createShoppingCartState.loading ||
    !!createShoppingCartState.data?.redirect;

  useEffect(() => {
    if (
      data &&
      searchRequest?.itinerary?.externalSelection &&
      !data.itinerarySearch.externalSelection
    ) {
      onSearchMapiError();
    }
  }, [data, onSearchMapiError, searchRequest]);

  return { loading };
};

export const useNextQueryState = (
  searchQueryState: SearchQueryResult,
  loading: boolean
): SearchQueryResult => {
  return useMemo(
    () => ({
      ...searchQueryState,
      // TODO - This weird cast is due to the old graphql client, remove once we go with the new one
      error:
        searchQueryState.errors ||
        (
          searchQueryState as unknown as {
            error: SearchQueryResult['errors'];
          }
        ).error,
      loading,
      data: loading ? null : searchQueryState.data,
    }),
    [searchQueryState, loading]
  );
};

export const useSkipQueryMetric = (
  skipQuery,
  searchRequest,
  alternativeMutationLaunched,
  alternativeSearchId,
  isNotAllowedHash
) => {
  const logger = useLogger();
  const baseMessage = 'SKIP_SEARCH_QUERY_reason';

  useEffect(() => {
    if (skipQuery && logger) {
      const getReason = () => {
        let reason = '';
        if (searchRequest === null) reason += '-search_request_is_null';
        if (alternativeMutationLaunched)
          reason += '-alternative_search_launched';
        if (alternativeSearchId !== null)
          reason += '-alternative_search_active';
        if (isNotAllowedHash) reason += '-is_not_allowed_url';
        return reason;
      };
      logger.warn(baseMessage + getReason());
    }
  }, [
    logger,
    skipQuery,
    searchRequest,
    alternativeMutationLaunched,
    alternativeSearchId,
    isNotAllowedHash,
  ]);
};
